import { ScreenHelp } from "@/components/Help";
import { ErrorState, LoadingState } from "@/components/States";
import { BarTrend, HBars } from "@/components/charts";
import { Card, Icon, Pill, SectionTitle, TableScroll } from "@/components/ui";
import { customerNameFrom, fmt, fmtComp, isoDate, quoteTotals } from "@/lib/data";
import { exportFileName, reportSheets } from "@/lib/excel";
import { useCustomers, useMonthly, useQuotes } from "@/lib/hooks";
import { cols, useIsMobile } from "@/lib/useViewport";
import { downloadXlsx } from "@/lib/xlsx";

const REPORTS_HELP = [
  "누적 견적 · 수주율 · 수주 금액 · 평균 영업이익률 KPI 를 요약합니다.",
  "월별 견적 제출 현황 차트로 전체 대비 수주 추이를 비교합니다.",
  "수주 실패 사유 분석에서 실패 원인 분포와 인사이트를 확인합니다.",
  "고객사별 통계 테이블에서 견적 건수 · 수주 · 수주율 · 견적 총액을 비교합니다.",
  "'보고서 엑셀' 버튼으로 통계를 엑셀로 내보냅니다.",
];

export const ReportsScreen = ({ onToast }: { onToast: (msg: string) => void }) => {
  const mobile = useIsMobile();
  const quotesRes = useQuotes();
  const customersRes = useCustomers();
  const monthlyRes = useMonthly();
  const wt = quotesRes.data.map((q) => ({
    ...q,
    t: quoteTotals(q.items),
    custName: customerNameFrom(customersRes.data, q.customerId),
  }));
  const decided = wt.filter((q) => q.status === "won" || q.status === "lost");
  const winRate = decided.length
    ? (decided.filter((q) => q.status === "won").length / decided.length) * 100
    : 0;

  // Loss reason analysis (demo-augmented so the chart reads well)
  const lossData = [
    { label: "가격 경쟁력", value: 7 },
    { label: "납기 불가", value: 3 },
    { label: "타사 선정", value: 2 },
    { label: "사양 미달", value: 1 },
    { label: "기타", value: 1 },
  ];

  const byCust: Record<string, { quotes: number; won: number; amount: number }> = {};
  for (const q of wt) {
    const k = q.custName;
    byCust[k] = byCust[k] || { quotes: 0, won: 0, amount: 0 };
    byCust[k].quotes++;
    byCust[k].amount += q.t.submit;
    if (q.status === "won") byCust[k].won++;
  }
  const custRows = Object.entries(byCust)
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.amount - a.amount);

  const wonAmount = wt.filter((q) => q.status === "won").reduce((a, q) => a + q.t.submit, 0);
  const avgMargin = wt.length ? wt.reduce((a, q) => a + q.t.profitRate, 0) / wt.length : 0;

  const kpis = [
    { l: "누적 견적", v: String(quotesRes.data.length), u: "건" },
    { l: "수주율", v: winRate.toFixed(0), u: "%", accent: true },
    { l: "수주 금액", v: fmtComp(wonAmount), u: "" },
    { l: "평균 영업이익률", v: avgMargin.toFixed(1), u: "%" },
  ];

  const loading = quotesRes.loading || monthlyRes.loading;
  const error = quotesRes.error || monthlyRes.error;
  if (loading) {
    return (
      <div style={{ padding: "24px 28px 56px" }}>
        <LoadingState />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: "24px 28px 56px" }}>
        <ErrorState
          error={error}
          onRetry={() => {
            void quotesRes.reload();
            void monthlyRes.reload();
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 28px 56px", display: "flex", flexDirection: "column", gap: 20 }}>
      <SectionTitle
        sub="주별 · 월별 견적 제출 현황과 수주 분석"
        right={
          <Pill
            variant="outlined"
            size="sm"
            icon={<Icon name="download" size={15} />}
            onClick={() => {
              downloadXlsx(
                exportFileName("보고서", isoDate(new Date())),
                reportSheets(quotesRes.data, customersRes.data, monthlyRes.data)
              );
              onToast("보고서를 엑셀로 내보냈습니다");
            }}
          >
            보고서 엑셀
          </Pill>
        }
      >
        통계 · 보고
      </SectionTitle>

      <ScreenHelp items={REPORTS_HELP} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: cols(mobile, "repeat(4,1fr)", "repeat(2,1fr)"),
          gap: 16,
        }}
      >
        {kpis.map((k) => (
          <Card key={k.l} pad={20}>
            <div style={{ fontSize: 12.5, color: "var(--text-dim)", marginBottom: 8 }}>{k.l}</div>
            <div
              style={{
                fontFamily: "var(--font-title)",
                fontWeight: 900,
                fontSize: mobile ? 26 : 32,
                color: k.accent ? "var(--brand)" : "var(--text-base)",
                letterSpacing: "-0.025em",
                whiteSpace: "nowrap",
              }}
            >
              {k.v}
              <span style={{ fontSize: 17 }}>{k.u && ` ${k.u}`}</span>
            </div>
          </Card>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: cols(mobile, "1.6fr 1fr"), gap: 16 }}>
        <Card pad={24}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
            <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 17 }}>
              월별 견적 제출 현황
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--text-muted)" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{ width: 10, height: 10, borderRadius: 2, background: "var(--bg-card)" }}
                />
                전체
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{ width: 10, height: 10, borderRadius: 2, background: "var(--brand)" }}
                />
                수주
              </span>
            </div>
          </div>
          <BarTrend data={monthlyRes.data} height={220} />
        </Card>
        <Card pad={24}>
          <div
            style={{
              fontFamily: "var(--font-title)",
              fontWeight: 800,
              fontSize: 17,
              marginBottom: 18,
            }}
          >
            수주 실패 사유 분석{" "}
            <span style={{ fontSize: 11.5, color: "var(--text-faint)", fontWeight: 400 }}>
              Loss Analysis
            </span>
          </div>
          <HBars data={lossData} fmt={(v) => `${v}건`} />
          <div
            style={{
              marginTop: 18,
              padding: "12px 14px",
              background: "rgba(243,114,127,0.08)",
              borderRadius: 6,
              fontSize: 12.5,
              color: "var(--text-muted)",
              lineHeight: 1.6,
            }}
          >
            <b style={{ color: "var(--text-negative)" }}>인사이트</b> — 실패의 50%가{" "}
            <b style={{ color: "var(--text-near)" }}>가격 경쟁력</b>에서 발생. 해당 품목군 단가 구조
            재검토가 필요합니다.
          </div>
        </Card>
      </div>

      <Card pad={0} style={{ overflow: "hidden" }}>
        <div
          style={{
            padding: "18px 24px 12px",
            fontFamily: "var(--font-title)",
            fontWeight: 800,
            fontSize: 17,
          }}
        >
          고객사별 통계
        </div>
        <TableScroll>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <thead style={{ background: "var(--bg-card-hover)" }}>
              <tr
                style={{
                  color: "var(--text-dim)",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                }}
              >
                <th style={{ textAlign: "left", padding: "10px 24px", fontWeight: 700 }}>고객사</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700 }}>
                  견적 건수
                </th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700 }}>수주</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700 }}>
                  수주율
                </th>
                <th style={{ textAlign: "right", padding: "10px 24px", fontWeight: 700 }}>
                  견적 총액
                </th>
              </tr>
            </thead>
            <tbody>
              {custRows.map((r) => (
                <tr key={r.name} style={{ borderTop: "1px solid var(--bg-surface-2)" }}>
                  <td style={{ padding: "13px 24px", fontWeight: 700 }}>{r.name}</td>
                  <td
                    style={{
                      padding: "13px 12px",
                      textAlign: "right",
                      fontFamily: "monospace",
                      color: "var(--text-near)",
                    }}
                  >
                    {r.quotes}
                  </td>
                  <td
                    style={{
                      padding: "13px 12px",
                      textAlign: "right",
                      fontFamily: "monospace",
                      color: "var(--brand)",
                    }}
                  >
                    {r.won}
                  </td>
                  <td style={{ padding: "13px 12px", textAlign: "right", fontFamily: "monospace" }}>
                    {r.quotes ? Math.round((r.won / r.quotes) * 100) : 0}%
                  </td>
                  <td
                    style={{
                      padding: "13px 24px",
                      textAlign: "right",
                      fontFamily: "monospace",
                      fontWeight: 700,
                    }}
                  >
                    {fmt(r.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      </Card>
    </div>
  );
};
